import React, { useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import Card from '../../components/Card';
import FormInput from '../../components/FormInput';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import styled from 'styled-components';

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
`;

const SettingsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 500px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const AdminSettings = () => {
  const { user, updatePassword } = useAdminAuth();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setError('Current password is required');
      return false;
    }
    if (!passwordData.newPassword) {
      setError('New password is required');
      return false;
    }
    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return false;
    }
    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);
    try {
      await updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setSuccess('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader>
        <Title>Admin Settings</Title>
        <Subtitle>Manage your account settings</Subtitle>
      </PageHeader>

      <SettingsSection>
        <SectionTitle>Account Information</SectionTitle>
        <Card>
          <div style={{ padding: '1rem' }}>
            <p><strong>Username:</strong> {user?.username}</p>
            <p><strong>Email:</strong> {user?.email}</p>
            <p><strong>Role:</strong> {user?.role}</p>
          </div>
        </Card>
      </SettingsSection>

      <SettingsSection>
        <SectionTitle>Change Password</SectionTitle>
        <Card>
          {error && (
            <Alert 
              type="error" 
              message={error} 
              onClose={() => setError('')} 
              style={{ marginBottom: '1rem' }}
            />
          )}
          
          {success && (
            <Alert 
              type="success" 
              message={success} 
              onClose={() => setSuccess('')} 
              style={{ marginBottom: '1rem' }}
            />
          )}

          <Form onSubmit={handlePasswordSubmit}>
            <FormGroup>
              <FormInput
                label="Current Password"
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                label="New Password"
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                label="Confirm New Password"
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormActions>
              <Button 
                type="submit" 
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </FormActions>
          </Form>
        </Card>
      </SettingsSection>
    </div>
  );
};

export default AdminSettings;
